import { ComplaintForm } from '@client/components/complaints/complaint-form';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Card } from 'antd';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/compliance/complaints/add')({
    staticData: {
        metadata: {
            title: 'New complaint',
        },
        ui: {
            widthVariant: 'compact',
            // Wider than the compact default (w-4xl) so the description
            // textarea and the paired fields get breathing room.
            className: 'w-6xl max-w-full',
        },
    },
    validateSearch: zodValidator(
        z.object({
            messageClassificationId: z.coerce.bigint().optional().catch(undefined),
            // Set when logging a complaint from a compliance audit finding
            // (CUR-168 / REQ-FND-7) — links the new complaint back to the finding.
            complianceAuditFindingId: z.coerce.bigint().optional().catch(undefined),
            // claimId may flow in from the auto-detect drawer when the
            // message mentions a single resolvable claim — pre-links the
            // complaint to that claim and lets ComplaintForm's claim
            // fetch prefill `policyNo`.
            claimId: z.coerce.bigint().optional().catch(undefined),
            incident: z
                .object({
                    incidentId: z.coerce.bigint().optional().catch(undefined),
                    handlingPartyIds: z.array(z.coerce.bigint()).optional().catch(undefined),
                    ownerId: z.coerce.bigint().optional().catch(undefined),
                    products: z.array(z.number()).optional().catch(undefined),
                    policyNo: z.string().optional().catch(undefined),
                    externalClaimId: z.string().optional().catch(undefined),
                })
                .optional()
                .catch(undefined),
        }),
    ),
    component: RouteComponent,
});

function RouteComponent() {
    const orgId = useOrgId();
    const { messageClassificationId, complianceAuditFindingId, claimId, incident } = Route.useSearch();
    const navigate = useNavigate();

    return (
        <Card>
            <div className="pb-6">
                <h2 className="m-0 font-semibold text-foreground text-xl">New complaint</h2>
            </div>
            <ComplaintForm
                claimId={claimId}
                messageClassificationId={messageClassificationId}
                complianceAuditFindingId={complianceAuditFindingId}
                incidentData={incident}
                onCancel={() => navigate({ moveInHistory: 'back' })}
                onAfterSaveAccessDenied={() => {
                    navigate({
                        to: '/console/$orgId/compliance/complaints',
                        params: {
                            orgId,
                        },
                    });
                }}
                onAfterSaveCancel={({ id: complaintId }) =>
                    incident?.incidentId
                        ? navigate({
                              to: '/console/$orgId/compliance/incidents/details/$id/manage/complaints',
                              params: {
                                  orgId,
                                  id: incident.incidentId,
                              },
                          })
                        : navigate({
                              to: '/console/$orgId/compliance/complaints/details/$id/manage',
                              params: {
                                  orgId,
                                  id: complaintId,
                              },
                          })
                }
            />
        </Card>
    );
}
